import { ConnectionRoleType, ItemType, SubjectOrObject } from "../types";

// Define tabs with their labels and colors (action-oriented for members)
//   { type: "creator", label: "I'm making", color: "#8B5CF6" },
//   { type: "collaborator", label: "I'm part of", color: "#3B82F6" },
//   { type: "participant", label: "I'm joining", color: "#10B981" },
export const memberPageTabLabels: {
  type: ConnectionRoleType;
  label: string;
  color: string;
}[] = [
  { type: "creator", label: "Making", color: "#8B5CF6" },
  { type: "collaborator", label: "Contributing", color: "#3B82F6" },
  { type: "participant", label: "Participating", color: "#10B981" },
];

export const eventPageTabLabels: {
  type: ConnectionRoleType;
  label: string;
  color: string;
}[] = [
  { type: "creator", label: "Organizers", color: "#8B5CF6" },
  { type: "collaborator", label: "Contributors", color: "#3B82F6" },
  { type: "participant", label: "Attendees", color: "#10B981" },
];

export const productPageTabLabels: {
  type: ConnectionRoleType;
  label: string;
  color: string;
}[] = [
  { type: "creator", label: "Creators", color: "#8B5CF6" },
  { type: "collaborator", label: "Contributors", color: "#3B82F6" },
  { type: "participant", label: "Customers", color: "#10B981" },
];

export const servicePageTabLabels: {
  type: ConnectionRoleType;
  label: string;
  color: string;
}[] = [
  { type: "creator", label: "Providers", color: "#8B5CF6" },
  { type: "collaborator", label: "Contributors", color: "#3B82F6" },
  { type: "participant", label: "Clients", color: "#10B981" },
];

export const projectPageTabLabels: {
  type: ConnectionRoleType;
  label: string;
  color: string;
}[] = [
  { type: "creator", label: "Organizers", color: "#8B5CF6" },
  { type: "collaborator", label: "Team Members", color: "#3B82F6" },
  { type: "participant", label: "Participants", color: "#10B981" },
];

type RoleTypeLabels = Record<ConnectionRoleType, string>;
type RoleLabelSet = Record<ItemType, RoleTypeLabels>;
type TwoWayRoleLabelSet = Record<SubjectOrObject, RoleLabelSet>;

// Depending on the type of offering, different strings may be used
export const memberLabelsOnOfferings: Record<ItemType, RoleTypeLabels> = {
  event: {
    creator: "organizer",
    collaborator: "presenter",
    participant: "attendee",
    listed: "listed",
  },
  product: {
    creator: "creator",
    collaborator: "contributor",
    participant: "user",
    listed: "listed",
  },
  project: {
    creator: "organizer",
    collaborator: "team member",
    participant: "participant",
    listed: "listed",
  },
  service: {
    creator: "provider",
    collaborator: "assistant",
    participant: "client",
    listed: "listed",
  },
  creative: {
    creator: "creator",
    collaborator: "contributor",
    participant: "audience",
    listed: "listed",
  },
  person: {
    creator: "creator", // If a member 'creates' an organization member, they are the creator
    collaborator: "collaborator", // If a member 'collaborates' with another member, they are a collaborator
    participant: "person", // If a member 'participates' in an organization member, they are a member
    listed: "listed",
  },
  organization: {
    creator: "admin",
    collaborator: "moderator",
    participant: "person",
    listed: "listed",
  },
  article: {
    creator: "author",
    collaborator: "contributor",
    participant: "reader",
    listed: "listed",
  },
  course: {
    creator: "instructor",
    collaborator: "teaching assistant",
    participant: "student",
    listed: "listed",
  },
};

export const roleLabels: TwoWayRoleLabelSet = {
  subject: {
    event: {
      creator: "",
      collaborator: "",
      participant: "",
      listed: "listed",
    },
    product: {
      creator: "",
      collaborator: "",
      participant: "",
      listed: "listed",
    },
    project: {
      creator: "",
      collaborator: "",
      participant: "",
      listed: "listed",
    },
    service: {
      creator: "",
      collaborator: "",
      participant: "",
      listed: "listed",
    },
    creative: {
      creator: "",
      collaborator: "",
      participant: "",
      listed: "listed",
    },
    person: {
      creator: "Making",
      collaborator: "Contributing",
      participant: "Participating",
      listed: "listed",
    },
    organization: {
      creator: "Admin",
      collaborator: "Moderator",
      participant: "Member",
      listed: "listed",
    },
    article: {
      creator: "author",
      collaborator: "contributor",
      participant: "reader",
      listed: "listed",
    },
    course: {
      creator: "instructor",
      collaborator: "teaching assistant",
      participant: "student",
      listed: "listed",
    },
  },
  object: {
    event: {
      creator: "Organizers",
      collaborator: "Contributors",
      participant: "Attendees",
      listed: "listed",
    },
    product: {
      creator: "Creators",
      collaborator: "Contributors",
      participant: "Customers",
      listed: "listed",
    },
    project: {
      creator: "Organizers",
      collaborator: "Contributors",
      participant: "Participants",
      listed: "listed",
    },
    service: {
      creator: "Providers",
      collaborator: "Contributors",
      participant: "Clients",
      listed: "listed",
    },
    creative: {
      creator: "Creators",
      collaborator: "Contributors",
      participant: "Audience",
      listed: "listed",
    },
    person: {
      creator: "Creators", // If a member 'creates' an organization member, they are the creator
      collaborator: "Collaborators", // If a member 'collaborates' with another member, they are a collaborator
      participant: "Members", // If a member 'participates' in an organization member, they are a member
      listed: "listed",
    },
    organization: {
      creator: "Admins",
      collaborator: "Moderators",
      participant: "Members",
      listed: "listed",
    },
    article: {
      creator: "Authors",
      collaborator: "Contributors",
      participant: "Readers",
      listed: "listed",
    },
    course: {
      creator: "Instructors",
      collaborator: "Teaching Assistants",
      participant: "Students",
      listed: "listed",
    },
  },
};

export function getLabel(
  itemType: ItemType,
  roleType: ConnectionRoleType,
  isSubject: boolean = true
): string {
  const subjectOrObject: SubjectOrObject = isSubject ? "subject" : "object";
  return roleLabels[subjectOrObject][itemType][roleType];
}
